import React, { useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { initSiteBehavior } from './behavior';

// Preserve inline styles verbatim (trusted first-party markup uses CSS custom
// properties like `--w` that DOMPurify's CSS filter would otherwise drop).
DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
  if (data.attrName === 'style') {
    data.forceKeepAttr = true;
  }
});

const SANITIZE_CONFIG = {
  USE_PROFILES: { html: true, svg: true, svgFilters: true },
  ADD_ATTR: ['target'],
  ADD_TAGS: ['use'],
};

// Map source .html filenames to SPA routes
const ROUTE_MAP = {
  'index.html': '/',
  'our-approach.html': '/our-approach',
  'what-we-do.html': '/what-we-do',
  'people-and-partners.html': '/people-and-partners',
  'transparency.html': '/transparency',
  'work-with-us.html': '/work-with-us',
  'contact.html': '/contact',
};

function decodeEntities(str) {
  // Strip any markup first, then decode HTML entities from the plain text.
  const clean = DOMPurify.sanitize(str, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  const el = document.createElement('textarea');
  el.innerHTML = clean;
  return el.value;
}

export default function Page({ html, title, description }) {
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Sanitize the first-party HTML fragment once per page (XSS defense-in-depth).
  const sanitizedHtml = useMemo(() => DOMPurify.sanitize(html, SANITIZE_CONFIG), [html]);

  // Set document title (decode HTML entities) and per-page meta description
  useEffect(() => {
    if (title) document.title = decodeEntities(title);
    if (description) {
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', 'description');
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', description);
    }
  }, [title, description]);

  // Run the site behavior after content is injected
  useEffect(() => {
    const cleanup = initSiteBehavior();
    return cleanup;
  }, [html]);

  // Scroll handling on route/hash change
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      // wait for content paint
      const t = setTimeout(() => {
        const target = document.getElementById(id);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else window.scrollTo(0, 0);
      }, 60);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash, html]);

  // Intercept internal link clicks for SPA navigation
  const onClick = (e) => {
    const a = e.target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href) return;
    if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('http')) return;

    // Pure in-page hash on current page
    if (href.startsWith('#')) {
      e.preventDefault();
      const id = href.slice(1);
      const target = document.getElementById(id);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // page.html or page.html#hash
    const [file, hash] = href.split('#');
    const route = ROUTE_MAP[file];
    if (route !== undefined) {
      e.preventDefault();
      navigate(route + (hash ? '#' + hash : ''));
    }
  };

  return (
    <div ref={ref} onClick={onClick} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
  );
}
